import React, { createContext, useCallback, useContext, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

const MobileContentsContext = createContext(null);

// Counts page visits (path changes) across every route, including ones without the
// practice sidebar, so practice A → pricing → A is a new visit to A. Module-level and
// updated during render: idempotent, so Strict Mode's double render counts once, and
// the sidebar's effects (which run before this provider's) already see it.
let lastVisitedPath = null;
let pathVisit = 0;

/** Lives above route layouts because changing practice modes can remount the sidebar. */
export function MobileContentsProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const url = `${location.pathname}${location.search}${location.hash}`;
  const routeKey = location.key ?? url;
  if (location.pathname !== lastVisitedPath) {
    lastVisitedPath = location.pathname;
    pathVisit += 1;
  }
  const [state, setState] = useState({ routeKey, open: false, destination: null });
  let current = state;
  if (state.routeKey !== routeKey) {
    current = {
      routeKey,
      open: state.open && navigationType !== 'POP' && state.destination === url,
      destination: null,
    };
    // Reconcile before rendering a newly mounted sidebar, including in Strict Mode.
    setState(current);
  }

  const setContentsOpen = useCallback((open) => {
    setState((previous) => ({ ...previous, open, destination: null }));
  }, []);
  const navigateKeepingContents = (to) => {
    if (to === url) return;
    setState((previous) => ({ ...previous, destination: previous.open ? to : null }));
    navigate(to);
  };

  return (
    <MobileContentsContext.Provider
      value={{ contentsOpen: current.open, setContentsOpen, navigateKeepingContents, pathVisit }}
    >
      {children}
    </MobileContentsContext.Provider>
  );
}

export const useMobileContents = () => useContext(MobileContentsContext);
